// ?use_ios_ui (2026-10-17) — the iOS 0.7.0 overlay tree, twin of iOS
// ManualPlacementView (branch wheel-sizing). Rendering only: every value
// arrives as a prop and every gesture leaves through `actions`, so App
// owns the stores and this file owns the layout.
//
// Android keeps two things iOS does not have: the Start AR splash before
// it (a WebXR session needs a user gesture), and the scanning overlay
// beside it (WebXR has to find planes first) — the framing ring waits on
// `isTrackingGood`.

import { useState } from 'react'
import { CurrentWheelSizeCard } from '@/components/ios-ui/current-wheel-size-card/current-wheel-size-card'
import { LoadStatusCard } from '@/components/ios-ui/load-status-card/load-status-card'
import { FramedPlacementOverlay } from '@/components/ios-ui/framed-placement-overlay/framed-placement-overlay'
import { IosTopBar } from '@/components/ios-ui/ios-top-bar/ios-top-bar'
import { WheelInfoModal } from '@/components/ios-ui/wheel-info-modal/wheel-info-modal'
import { CloseConfirm } from '@/components/ios-ui/close-confirm/close-confirm'
import { PoweredByFooter } from '@/components/ios-ui/powered-by-footer/powered-by-footer'
import { IosAdjustmentPanel } from '@/components/ios-ui/ios-adjustment-panel/ios-adjustment-panel'
import { Toast } from '@/components/toast/toast'
import type { IosPlacementState } from '@/controllers/ios-placement-controller'
import type { WheelPlacementControllerState } from '@/controllers/wheel-placement-controller'
import type { WheelLoaderState } from '@/services/wheel-loader'
import { framedReticle } from '@/models/framed-reticle'
import { isDeadEndLoad } from '@/lib/isDeadEndLoad'
import { closeConfirmCopy } from '@/lib/closeConfirmCopy'
import { padStep } from '@/ar/pad-step'
import type { ScreenQuadrant } from '@/ar/touch-gestures'

/** Room under the panel for the footer chip (5px off the edge + its ~25px
 *  height), as iOS reserves it. The panel's own 16px padding then keeps its
 *  tiles above Android's gesture bar; env(safe-area-inset-bottom) reads 0
 *  inside Chrome's dom-overlay, so a literal does the work. */
const PANEL_BOTTOM_CLEARANCE_PX = 33

export interface IosUiOverlayActions {
  placeFramed: () => void
  cancelPlaceUI: () => void
  openPlaceUI: () => void
  setCurrentWheelSize: (inches: number) => void
  dismissSizeQuestion: () => void
  setSelectedWheelSize: (inches: number) => void
  deleteSelected: () => void
  adjustHeight: (delta: number) => void
  adjustHorizontal: (delta: number) => void
  adjustDepth: (delta: number) => void
  adjustTilt: (delta: number) => void
  adjustYaw: (delta: number) => void
  retryLoad: () => void
  cancelLoad: () => void
  /** Leave for the storefront: Done (true), a dead end or a cancelled load (false). */
  exit: (success: boolean) => void
  /** The confirmed close. */
  close: () => void
  /** Takes an AR photo. */
  camera: () => void
}

export interface IosUiOverlayProps {
  loader: Pick<
    WheelLoaderState,
    'pollingError' | 'downloadError' | 'isPollingAssembler' | 'isLoading' | 'hasLoadedModel' | 'downloadProgress' | 'wheelInfo'
  >
  placement: Pick<WheelPlacementControllerState, 'placedWheelCount' | 'selectedWheelId' | 'phase' | 'adjustmentsForSelected'>
  ios: IosPlacementState
  isTrackingGood: boolean
  isLandscape: boolean
  viewSize: { width: number; height: number }
  hasReturnURL: boolean
  /** There is a page to go back to, so a dead end can offer one. */
  exitIsDeadEndCapable: boolean
  closing: boolean
  toastMessage: string | null
  onToastDismiss: () => void
  version: string
  maxCamberRad: number
  heightStep: number
  horizontalStep: number
  /** Quarter turn between the DOM and the rendered frame (main.ts's
   *  renderedQuadrant) — the pad's arrows follow the user's view. */
  getRenderedQuadrant: () => ScreenQuadrant
  actions: IosUiOverlayActions
}

export function IosUiOverlay(props: IosUiOverlayProps) {
  const { loader, placement, ios, actions } = props
  const [showWheelInfo, setShowWheelInfo] = useState(false)
  const [confirmingClose, setConfirmingClose] = useState(false)

  const card = loadCard(props)
  const isReticleVisible =
    card == null &&
    props.isTrackingGood &&
    placement.phase.kind === 'idle' &&
    loader.hasLoadedModel &&
    ios.canAddWheel &&
    ios.isPlaceUIOpen &&
    props.viewSize.width > 0
  const selectedId = placement.selectedWheelId
  const closeCopy = closeConfirmCopy({ hasReturnURL: props.hasReturnURL })

  return (
    // display: contents — no box of its own, so it never takes a touch. The
    // rule switches off Chrome's tap highlight, the blue flash it draws over
    // anything clickable (dev, 2026-10-19: the container "highlights blue").
    <div data-testid="ios-ui-root" style={{ display: 'contents' }}>
      <style>{TAP_HIGHLIGHT_OFF_CSS}</style>
      {isReticleVisible && (
        <FramedPlacementOverlay
          reticle={framedReticle({ viewSize: props.viewSize, isLandscape: props.isLandscape })}
          wheelIndex={placement.placedWheelCount + 1}
          isLandscape={props.isLandscape}
          onPlace={actions.placeFramed}
          onCancel={actions.cancelPlaceUI}
        />
      )}

      <IosTopBar
        showAdd={!ios.isPlaceUIOpen && ios.canAddWheel}
        showDone={props.hasReturnURL}
        wheelInfo={loader.wheelInfo}
        onAdd={actions.openPlaceUI}
        onClose={() => setConfirmingClose(true)}
        onDone={() => actions.exit(true)}
        onPillTap={() => setShowWheelInfo(true)}
        closing={props.closing}
        isLandscape={props.isLandscape}
        onCamera={actions.camera}
      />

      {selectedId != null && (
        <div
          data-testid={props.isLandscape ? 'ios-panel-landscape' : 'ios-panel-portrait'}
          style={
            props.isLandscape
              ? { position: 'fixed', top: 8, bottom: 8, right: 0, display: 'flex', alignItems: 'center', zIndex: 20 }
              : { position: 'fixed', left: 0, right: 0, bottom: PANEL_BOTTOM_CLEARANCE_PX, zIndex: 20 }
          }
        >
          <IosAdjustmentPanel
            isLandscape={props.isLandscape}
            sizeOptions={ios.sizeOptionsForSelected}
            shownSizeInches={ios.shownSizeForSelected}
            onSizeChange={actions.setSelectedWheelSize}
            currentWheelSizeInches={ios.currentWheelSize?.inches ?? null}
            ringSizeInches={ios.ringSizeInchesForSelected}
            onCurrentWheelSizeChange={actions.setCurrentWheelSize}
            onDelete={actions.deleteSelected}
            onHeightChange={actions.adjustHeight}
            onHorizontalChange={actions.adjustHorizontal}
            onDepthChange={actions.adjustDepth}
            onTiltChange={actions.adjustTilt}
            onYawChange={actions.adjustYaw}
            tiltAngleRad={placement.adjustmentsForSelected.tiltAngle}
            yawAngleRad={placement.adjustmentsForSelected.yawAngle}
            maxCamberRad={props.maxCamberRad}
            heightStep={props.heightStep}
            horizontalStep={props.horizontalStep}
            onPad={(direction) => {
              const { height, horizontal } = padStep({
                direction,
                quadrant: props.getRenderedQuadrant(),
                heightStep: props.heightStep,
                horizontalStep: props.horizontalStep,
              })
              if (height !== 0) actions.adjustHeight(height)
              if (horizontal !== 0) actions.adjustHorizontal(horizontal)
            }}
          />
        </div>
      )}

      <PoweredByFooter version={props.version} />

      {card}

      <Toast
        message={props.toastMessage}
        onDismiss={props.onToastDismiss}
        durationMs={2000}
        style={{ bottom: 120, background: 'rgba(0, 0, 0, 0.7)', padding: '10px 20px', borderRadius: 999, zIndex: 60 }}
      />

      {showWheelInfo && (
        <WheelInfoModal
          brandLogoURL={loader.wheelInfo.brandLogoURL}
          modelName={loader.wheelInfo.modelName}
          onDismiss={() => setShowWheelInfo(false)}
        />
      )}

      {confirmingClose && (
        <CloseConfirm
          title={closeCopy.title}
          message={closeCopy.message}
          onCancel={() => setConfirmingClose(false)}
          onConfirm={() => {
            setConfirmingClose(false)
            actions.close()
          }}
        />
      )}
    </div>
  )
}

const TAP_HIGHLIGHT_OFF_CSS =
  '[data-testid="ios-ui-root"], [data-testid="ios-ui-root"] * { -webkit-tap-highlight-color: transparent; }'

/** The one card in front of the AR view, in iOS's priority order. Errors
 *  first so Retry and Cancel stay reachable; the size question outranks the
 *  progress cards so it covers them rather than competing. */
function loadCard({ loader, ios, actions, exitIsDeadEndCapable }: IosUiOverlayProps) {
  const back = exitIsDeadEndCapable && isDeadEndLoad(loader) ? () => actions.exit(false) : undefined
  if (!loader.hasLoadedModel && loader.pollingError != null) {
    return (
      <LoadStatusCard
        kind="error"
        errorTitle="Failed to Load"
        error={loader.pollingError}
        onRetry={actions.retryLoad}
        onCancel={actions.cancelLoad}
        onBackToProductPage={back}
      />
    )
  }
  if (!loader.hasLoadedModel && loader.downloadError != null) {
    return (
      <LoadStatusCard
        kind="error"
        errorTitle="Download Failed"
        error={loader.downloadError}
        onRetry={actions.retryLoad}
        onCancel={actions.cancelLoad}
        onBackToProductPage={back}
      />
    )
  }
  if (ios.isAskingCurrentWheelSize) {
    return (
      <CurrentWheelSizeCard answer={ios.currentWheelSize} onAnswer={actions.setCurrentWheelSize} onDismiss={actions.dismissSizeQuestion} />
    )
  }
  if (loader.isPollingAssembler) {
    return <LoadStatusCard kind="polling" onRetry={actions.retryLoad} onCancel={actions.cancelLoad} />
  }
  if (loader.isLoading && !loader.hasLoadedModel) {
    return <LoadStatusCard kind="download" progress={loader.downloadProgress} onRetry={actions.retryLoad} onCancel={actions.cancelLoad} />
  }
  return null
}
